import { useState, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

// ─── Date helpers ──────────────────────────────────────────────────────────────

function today(): Date {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function add(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fromISO(s: string): Date | null {
  if (!s) return null
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function same(a: Date | null, b: Date | null): boolean {
  return !!a && !!b && a.toDateString() === b.toDateString()
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const DOW_LABELS = ['S','M','T','W','T','F','S']

function rangeLabel(from: Date | null, to: Date | null): string {
  if (!from) return 'Any dates'
  if (!to || same(from, to)) return `${MONTHS[from.getMonth()]} ${from.getDate()}`
  if (from.getMonth() === to.getMonth())
    return `${MONTHS[from.getMonth()]} ${from.getDate()} – ${to.getDate()}`
  return `${MONTHS[from.getMonth()]} ${from.getDate()} – ${MONTHS[to.getMonth()]} ${to.getDate()}`
}

// ─── Preset computation ────────────────────────────────────────────────────────

interface Preset { id: string; label: string; from: Date; to: Date; rec?: boolean }

function buildPresets(): Preset[] {
  const now = today()
  const dow = now.getDay() // 0=Sun … 6=Sat

  // Smart rollover: Sunday → use *next* weekend
  const daysToSat = dow === 0 ? 6 : 6 - dow
  const sat = add(now, daysToSat)
  const sun = add(sat, 1)

  // This week: Mon–Sun
  const daysToMon = dow === 0 ? -6 : 1 - dow
  const mon = add(now, daysToMon)
  const weekSun = add(mon, 6)

  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastOfMonth  = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  return [
    { id: 'today',   label: 'Today',        from: now,          to: now },
    { id: 'tomorrow',label: 'Tomorrow',     from: add(now, 1),  to: add(now, 1) },
    { id: 'weekend', label: 'This weekend', from: sat,          to: sun },
    { id: 'week',    label: 'This week',    from: mon,          to: weekSun },
    { id: 'month',   label: 'This month',   from: firstOfMonth, to: lastOfMonth, rec: true },
  ]
}

function matchPreset(presets: Preset[], from: Date | null, to: Date | null): string | null {
  if (!from || !to) return null
  return presets.find(p => same(p.from, from) && same(p.to, to))?.id ?? null
}

// ─── Mini calendar ─────────────────────────────────────────────────────────────

function MiniCal({
  view, setView, from, to, onPick,
}: {
  view: Date
  setView: (d: Date) => void
  from: Date | null
  to: Date | null
  onPick: (d: Date) => void
}) {
  const Y = view.getFullYear()
  const M = view.getMonth()
  const firstOfMonth = new Date(Y, M, 1)
  const gridStart = new Date(Y, M, 1 - firstOfMonth.getDay())
  const cells = Array.from({ length: 42 }, (_, i) =>
    new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i),
  )
  const now = today()

  return (
    <div style={{ padding: '4px 6px 2px' }}>
      {/* Month header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '2px 2px 12px' }}>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
          {MONTHS[M]} {Y}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            type="button"
            onClick={() => setView(new Date(Y, M - 1, 1))}
            style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--ink-2)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => setView(new Date(Y, M + 1, 1))}
            style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--ink-2)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Day-of-week row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 2 }}>
        {DOW_LABELS.map((d, i) => (
          <span key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>{d}</span>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((d, i) => {
          const muted     = d.getMonth() !== M
          const isFrom    = same(d, from)
          const isTo      = same(d, to)
          const endpoint  = isFrom || isTo
          const hasRange  = from && to && !same(from, to)
          const between   = hasRange ? (d.getTime() > from!.getTime() && d.getTime() < to!.getTime()) : false
          const inRange   = hasRange ? (between || isFrom || isTo) : false
          const isToday   = same(d, now)

          return (
            <div key={i} style={{ position: 'relative', height: 38, display: 'grid', placeItems: 'center' }}>
              {/* Range fill bar */}
              {inRange && (
                <div style={{
                  position: 'absolute', top: 3, bottom: 3, zIndex: 0,
                  left:  (hasRange && isFrom) ? '50%' : 0,
                  right: (hasRange && isTo)   ? '50%' : 0,
                  background: 'var(--primary-soft)',
                }} />
              )}
              <div
                onClick={() => onPick(d)}
                style={{
                  position: 'relative', zIndex: 1,
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'grid', placeItems: 'center',
                  fontSize: 13, fontVariantNumeric: 'tabular-nums',
                  cursor: 'pointer',
                  background: endpoint ? 'var(--primary)' : 'transparent',
                  color: endpoint ? '#fff' : muted ? 'var(--muted)' : 'var(--ink)',
                  opacity: muted && !endpoint ? 0.45 : 1,
                  boxShadow: isToday && !endpoint ? 'inset 0 0 0 1.5px var(--primary)' : 'none',
                }}
              >
                {d.getDate()}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── WhenPicker ────────────────────────────────────────────────────────────────

interface WhenPickerProps {
  from: string
  to: string
  onChange: (from: string, to: string) => void
}

export function WhenPicker({ from: fromStr, to: toStr, onChange }: WhenPickerProps) {
  const PRESETS = buildPresets()
  const month    = PRESETS.find(p => p.id === 'month')!

  const initFrom = fromStr ? fromISO(fromStr) : month.from
  const initTo   = toStr   ? fromISO(toStr)   : month.to

  const [open, setOpen] = useState(false)
  const [sel,  setSel]  = useState<string | null>(
    fromStr ? matchPreset(PRESETS, initFrom, initTo) : 'month',
  )
  const [from, setFrom] = useState<Date | null>(initFrom)
  const [to,   setTo]   = useState<Date | null>(initTo)
  const [view, setView] = useState<Date>(
    new Date((initFrom ?? month.from).getFullYear(), (initFrom ?? month.from).getMonth(), 1),
  )
  const [pick, setPick] = useState<0 | 1>(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync when parent resets from/to
  useEffect(() => {
    const f = fromStr ? fromISO(fromStr) : null
    const t = toStr   ? fromISO(toStr)   : null
    setFrom(f ?? month.from)
    setTo(t ?? month.to)
    setSel(fromStr ? matchPreset(PRESETS, f, t) : 'month')
  }, [fromStr, toStr]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function choose(p: Preset) {
    setSel(p.id)
    setFrom(p.from)
    setTo(p.to)
    setView(new Date(p.from.getFullYear(), p.from.getMonth(), 1))
  }

  function pickDay(d: Date) {
    if (pick === 0 || (from && d.getTime() < from.getTime())) {
      setFrom(d); setTo(null); setPick(1); setSel(null)
    } else {
      const newTo = d
      setTo(newTo); setPick(0); setSel(matchPreset(PRESETS, from, newTo))
    }
  }

  function handleApply() {
    const f = from ?? month.from
    const t = to ?? f
    onChange(toISO(f), toISO(t))
    setOpen(false)
  }

  function handleClear() {
    setFrom(null); setTo(null); setSel(null)
    onChange('', '')
    setOpen(false)
  }

  const activePreset = PRESETS.find(p => p.id === sel)
  const labelMain = activePreset ? activePreset.label : from ? 'Custom' : 'Any dates'
  const labelSub  = from ? rangeLabel(from, to) : null

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Trigger — mimics the existing SearchPage "When" inner layout */}
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Date range: ${labelMain}${labelSub ? ` · ${labelSub}` : ''}`}
        onClick={() => setOpen(v => !v)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
      >
        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', lineHeight: 1, marginBottom: 2 }}>
          When
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
            {labelMain}
          </span>
          {labelSub && (
            <span style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              · {labelSub}
            </span>
          )}
        </div>
      </button>

      {/* Popover */}
      {open && (
        <div
          role="dialog"
          aria-label="Pick a date range"
          style={{
            position: 'absolute', top: 'calc(100% + 14px)', left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 50, width: 320,
            background: '#fff',
            border: '1px solid var(--line-soft)',
            borderRadius: 16, padding: 8,
            boxShadow: '0 14px 38px rgba(60,50,35,.18)',
          }}
        >
          {/* Preset chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 5px 10px' }}>
            {PRESETS.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => choose(p)}
                style={{
                  border: `1px solid ${sel === p.id ? 'var(--primary)' : 'var(--line-soft)'}`,
                  background: sel === p.id ? 'var(--primary)' : '#fff',
                  borderRadius: 999,
                  padding: '7px 13px',
                  fontSize: 13, fontWeight: 600, font: 'inherit',
                  color: sel === p.id ? '#fff' : 'var(--ink-2)',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {p.label}
                {p.rec && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: sel === p.id ? 'rgba(255,255,255,.85)' : '#5e45a8',
                    background: sel === p.id ? 'rgba(255,255,255,.18)' : '#efeaf8',
                    padding: '2px 6px', borderRadius: 999,
                  }}>
                    Suggested
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Calendar */}
          <MiniCal view={view} setView={setView} from={from} to={to} onPick={pickDay} />

          {/* Footer */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderTop: '1px solid var(--line-soft)', marginTop: 6, padding: '10px 6px 4px',
          }}>
            <button
              type="button"
              onClick={handleClear}
              style={{
                border: 'none', background: 'none', cursor: 'pointer', font: 'inherit',
                fontSize: 13, fontWeight: 600, color: 'var(--muted)', padding: '6px 4px',
              }}
            >
              Clear
            </button>
            <span style={{
              fontSize: 13,
              fontWeight: from ? 600 : 500,
              color: from ? 'var(--ink)' : 'var(--muted)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {from ? rangeLabel(from, to) : 'Pick dates'}
            </span>
            <button
              type="button"
              onClick={handleApply}
              style={{
                border: 'none',
                background: 'linear-gradient(135deg, #3b4cca, #7c5cd3)',
                color: '#fff', cursor: 'pointer', font: 'inherit',
                fontSize: 13, fontWeight: 600, borderRadius: 999, padding: '9px 18px',
                boxShadow: '0 6px 18px rgba(59,76,202,.28)',
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
